import { upperFirst } from 'lodash';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useUserPermissions } from 'auth/hooks';
import { Breadcrumbs, PageTabs, PageTitle } from 'core/components';
import { LabeledValue } from 'core/elements';
import { AccessPermissions } from 'core/enums';
import { SelectionCountProvider } from 'core/states';
import { IBreadcrumbsItem, ITab } from 'core/types';
import { JobDetailsDrawerButton } from 'jobs/components/JobDetailsDrawer';
import { jobListPath } from 'jobs/routes';
import { IJob } from 'jobs/types';
import { getStatusColorMap } from 'jobs/utils';
import { useGetDtdProject } from 'projects/hooks';
import { projectDetailsPath } from 'projects/routes';
import { CustomerQuoteList, VendorQuoteList } from 'quotes/components';
import { QuoteEntityType, QuoteListType } from 'quotes/enums';

import { JobDetailsTab } from './enums';
import { CustomerList, Documents, LogList, MaterialList, VendorList } from './tabs';

enum TabValue {
  CUSTOMER_ORDERS = 'customerOrders',
  CUSTOMER_QUOTES = 'customerQuotes',
  DOCUMENTS = 'documents',
  MATERIAL_LIST = 'materialList',
  ORDERS = 'orders',
  PEOPLE = 'people',
  PEOPLE_CUSTOMERS = 'peopleCustomers',
  PEOPLE_VENDORS = 'peopleVendors',
  QUOTES = 'quotes',
  VENDOR_ORDERS = 'vendorOrders',
  VENDOR_QUOTES = 'vendorQuotes',
}

interface IProps {
  job: IJob;
}

export const JobDetails = ({ job }: IProps) => {
  const { t } = useTranslation();

  const { hasPermission } = useUserPermissions();

  const { record: dtdProject } = useGetDtdProject();
  const isJobInDtdProject = dtdProject?.id === job.projectId;

  const breadcrumbs: IBreadcrumbsItem[] = [
    {
      text: t('labelJobs'),
      to: jobListPath(),
    },
    {
      text: job.name,
    },
  ];

  const tabs: ITab[] = useMemo(() => {
    return [
      {
        label: t('labelJobPartList'),
        title: t('labelJobPartList'),
        value: JobDetailsTab.MATERIAL_LIST,
        content: <MaterialList job={job} />,
      },
      {
        label: t('labelQuotes'),
        value: TabValue.QUOTES,
        subTabs: [
          {
            label: t('labelCustomerQuotes'),
            title: t('labelCustomerQuotes'),
            value: TabValue.CUSTOMER_QUOTES,
            content: <CustomerQuoteList quoteListType={QuoteListType.JOB} job={job} />,
          },
          {
            label: t('labelVendorQuotes'),
            title: t('labelVendorQuotes'),
            value: TabValue.VENDOR_QUOTES,
            content: (
              <VendorQuoteList
                entityType={QuoteEntityType.QUOTE}
                isDtdProject={isJobInDtdProject}
                job={job}
                quoteListType={QuoteListType.JOB}
              />
            ),
          },
        ],
      },
      {
        label: t('labelOrders'),
        value: TabValue.ORDERS,
        subTabs: [
          {
            label: t('labelCustomerOrders'),
            title: t('labelCustomerOrders'),
            value: TabValue.CUSTOMER_ORDERS,
            content: (
              <CustomerQuoteList
                entityType={QuoteEntityType.ORDER}
                quoteListType={QuoteListType.JOB}
                job={job}
              />
            ),
          },
          {
            label: t('labelVendorOrders'),
            title: t('labelVendorOrders'),
            value: TabValue.VENDOR_ORDERS,
            content: (
              <VendorQuoteList
                entityType={QuoteEntityType.ORDER}
                isDtdProject={isJobInDtdProject}
                job={job}
                quoteListType={QuoteListType.JOB}
              />
            ),
          },
        ],
      },
      {
        label: t('labelPeople'),
        value: TabValue.PEOPLE,
        subTabs: [
          {
            label: t('labelCustomerContacts'),
            value: TabValue.PEOPLE_CUSTOMERS,
            content: <CustomerList jobId={job.id} projectId={job.projectId} />,
          },
          {
            label: t('labelVendorContacts'),
            value: TabValue.PEOPLE_VENDORS,
            content: <VendorList jobId={job.id} projectId={job.projectId} />,
          },
        ],
      },
      {
        label: t('labelLogList'),
        title: t('labelLogList'),
        value: JobDetailsTab.LOG_LIST,
        content: <LogList job={job} />,
        hide: !(job.logAccessAllowed && hasPermission(AccessPermissions.ACCESS_COMPANY_BOM_LOG)),
      },
      {
        label: t('labelDocuments'),
        value: TabValue.DOCUMENTS,
        content: <Documents job={job} />,
        hide: !(
          job.access?.viewAllowed &&
          hasPermission(AccessPermissions.ACCESS_COMPANY_BOM_DETAILS_VIEW)
        ),
      },
    ];
  }, [job, t, hasPermission, isJobInDtdProject]);

  const getChips = useCallback(() => {
    if (!job.status) {
      return [];
    }

    return [
      {
        label: upperFirst(job.status),
        color: getStatusColorMap([job.status])[job.status],
      },
    ];
  }, [job.status]);

  return (
    <>
      <Breadcrumbs breadcrumbs={breadcrumbs} />

      <PageTitle
        title={job.name}
        chips={getChips()}
        subtitle={
          <LabeledValue
            label={t('labelProject')}
            value={job.projectName}
            options={{ valueLink: projectDetailsPath(job.projectId), inline: true }}
          />
        }
        buttons={
          job.access?.viewAllowed &&
          hasPermission(AccessPermissions.ACCESS_COMPANY_BOM_DETAILS_VIEW)
            ? [<JobDetailsDrawerButton key="jobDetails" job={job} />]
            : undefined
        }
      />

      <SelectionCountProvider>
        <PageTabs tabs={tabs} defaultTab={JobDetailsTab.MATERIAL_LIST} ariaLabel="jobDetailsTabs" />
      </SelectionCountProvider>
    </>
  );
};
